import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This component processes the Emergent Google Auth callback. It reads session_id from the URL
// fragment (must use useLocation().hash — not window.location.hash — to stay reactive), calls our
// backend to exchange it for a server-set httpOnly cookie, and then redirects to home.
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setError("No session_id in URL");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/session", null, { headers: { "X-Session-ID": sessionId } });
        // Clear the hash so we don't reprocess on back/forward
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        await refresh();
        toast.success(`Welcome, ${data.user?.name || "friend"}`);
        nav("/", { replace: true, state: { user: data.user } });
      } catch (err) {
        const msg = formatApiError(err.response?.data?.detail) || "Google sign-in failed";
        setError(msg);
        toast.error(msg);
      }
    })();
  }, [location.hash, nav, refresh]);

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="auth-callback">
        {error ? (
          <div className="card-dark p-8">
            <div className="font-display uppercase text-3xl grad-text-hot">Sign-in failed</div>
            <p className="mt-3 text-sm text-text/70 font-mono break-words">{error}</p>
            <button onClick={() => nav("/login")} className="mt-6 btn-primary w-full justify-center">Back to login</button>
          </div>
        ) : (
          <div className="card-dark p-10">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-lime" />
            <div className="mt-4 font-display uppercase text-2xl">Signing you in…</div>
            <p className="mt-2 text-xs font-mono text-muted">Handshake with Google + Eventom.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
